/**
 * Billing math shared by backend (charge/offer computation, Stripe assertions) and frontend
 * (display estimates). The authoritative totals always come from Stripe — these formulas exist so
 * both apps predict/verify the SAME number Stripe will bill.
 */

/** One row of a graduated Stripe tier table; `upTo: null` marks the open-ended last tier. */
export interface GraduatedSeatTier {
  upTo: number | null;
  perSeatCents: number;
}

/** Total for a graduated tier table: each seat is billed at the rate of the tier it falls into. */
export function computeGraduatedSeatTotalCents(quantity: number, tiers: GraduatedSeatTier[]): number {
  let remaining = Math.max(0, quantity);
  let previousUpTo = 0;
  let totalCents = 0;
  for (const tier of tiers) {
    if (remaining <= 0) break;
    const tierCapacity = tier.upTo == null ? remaining : Math.max(0, tier.upTo - previousUpTo);
    const seatsInTier = Math.min(remaining, tierCapacity);
    totalCents += seatsInTier * tier.perSeatCents;
    remaining -= seatsInTier;
    previousUpTo = tier.upTo ?? previousUpTo;
  }
  return totalCents;
}

/** Discounted amount in integer cents, clamped at zero — the shared estimate/display formula. */
export function applyPercentDiscount(amountCents: number, percent: number): number {
  return Math.max(0, Math.round((amountCents * (100 - percent)) / 100));
}
